import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { SyncDiagnostics, SyncService, SyncTableDiagnostic } from '@vex/http-service/sync.service';

/** Displays live browser cache state and explicit synchronization recovery actions. */
@Component({
  selector: 'vex-sync-status-dialog',
  templateUrl: './sync-status-dialog.component.html',
  styleUrls: ['./sync-status-dialog.component.scss']
})
export class SyncStatusDialogComponent implements OnInit, OnDestroy {
  diagnostics: SyncDiagnostics;
  loading = false;
  error = '';
  syncingAll = false;
  repairingTable = '';
  private refreshTimer: any;

  constructor(
    public dialogRef: MatDialogRef<SyncStatusDialogComponent>,
    private syncService: SyncService
  ) {}

  ngOnInit(): void {
    this.refresh();
    this.refreshTimer = setInterval(() => this.refresh(true), 5000);
  }

  ngOnDestroy(): void {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
    }
  }

  /** Reloads diagnostics without starting or mutating synchronization. */
  async refresh(silent: boolean = false): Promise<void> {
    if (this.loading) {
      return;
    }
    this.loading = true;
    if (!silent) {
      this.error = '';
    }
    try {
      this.diagnostics = await this.syncService.getDiagnostics();
      if (!silent) {
        this.error = '';
      }
    } catch (error) {
      this.error = 'Local sync details could not be read. Close other tabs and try again.';
      console.error('Unable to load local sync diagnostics.', error);
    } finally {
      this.loading = false;
    }
  }

  /** Runs the same validated, cross-tab coordinated synchronization used at login. */
  async syncNow(): Promise<void> {
    if (this.syncingAll) {
      return;
    }
    this.syncingAll = true;
    this.error = '';
    try {
      const result = await this.syncService.syncAll(true);
      if (result.failed.length > 0) {
        this.error = `Sync could not complete for ${result.failed.join(', ')}. You can retry those tables below.`;
      }
      await this.refresh(true);
    } finally {
      this.syncingAll = false;
    }
  }

  /** Clears and rebuilds only the selected table from a fixed server snapshot. */
  async repair(table: SyncTableDiagnostic): Promise<void> {
    if (this.repairingTable) {
      return;
    }
    this.repairingTable = table.tableName;
    this.error = '';
    try {
      const succeeded = await this.syncService.repairTable(table.tableName);
      if (!succeeded) {
        this.error = `${table.tableLabel} could not be repaired. Its resumable progress has been preserved.`;
      }
      await this.refresh(true);
    } finally {
      this.repairingTable = '';
    }
  }

  /** Returns whether a table should expose the explicit rebuild action. */
  canRepair(table: SyncTableDiagnostic): boolean {
    return table.status === 'Failed' || table.status === 'Repair needed';
  }

  statusClass(table: SyncTableDiagnostic): string {
    return `status-${table.status.toLowerCase().replace(' ', '-')}`;
  }

  trackTable(index: number, table: SyncTableDiagnostic): string {
    return table.tableName;
  }
}
